import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { useState } from "react";
import {
  listUsers,
  createUser,
  updateUser,
  deleteUserAdmin,
  getUserById,
  softDeleteUserAdmin,
} from "../../../services/admin/userManagement";

function useAdminUserFetch() {
  const queryClient = useQueryClient();
  const [queryParams, setQueryParams] = useState({ page: 1, limit: 10 });

  const {
    data: usersData,
    isLoading: isGettingUsers,
    error: usersError,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ["adminUsers", queryParams],
    queryFn: () => listUsers(queryParams),
    keepPreviousData: true,
  });

  // Add a setter for params
  const setUsersQueryParams = (params) =>
    setQueryParams((prev) => ({ ...prev, ...params }));

  // Create user
  const { mutate: createUserFn, isLoading: isCreatingUser } = useMutation({
    mutationFn: (userData) => createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries(["adminUsers"]);
      toast.success("User created successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Error creating user");
    },
  });

  // Update user
  const { mutate: updateUserFn, isLoading: isUpdatingUser } = useMutation({
    mutationFn: ({ userId, userData }) => updateUser(userId, userData),
    onSuccess: () => {
      queryClient.invalidateQueries(["adminUsers"]);
      toast.success("User updated successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Error updating user");
    },
  });

  // Delete user (hard delete)
  const { mutate: deleteUserFn, isLoading: isDeletingUser } = useMutation({
    mutationFn: (userId) => deleteUserAdmin(userId),
    onSuccess: () => {
      queryClient.invalidateQueries(["adminUsers"]);
      toast.success("User deleted successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Error deleting user");
    },
  });

  // Soft delete user
  const { mutate: softDeleteUserFn, isLoading: isSoftDeletingUser } =
    useMutation({
      mutationFn: (userId) => softDeleteUserAdmin(userId),
      onSuccess: () => {
        queryClient.invalidateQueries(["adminUsers"]);
        toast.success("User soft-deleted successfully");
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || "Error soft-deleting user");
      },
    });

  // List users with custom params (manual fetch)
  const getUsersWithParams = async (params) => {
    try {
      return await listUsers(params);
    } catch (error) {
      throw error;
    }
  };

  // Add a TanStack Query hook for single user by ID
  function useUserById(userId, options = {}) {
    const { data, isLoading, error, refetch } = useQuery({
      queryKey: ["adminUser", userId],
      queryFn: () => getUserById(userId),
      enabled: !!userId,
      ...options,
    });

    return { data, isLoading, error, refetch };
  }
  return {
    // Queries
    users: usersData?.users || [],
    totalPages: usersData?.totalPages || 0,
    currentPage: usersData?.currentPage || 1,
    totalUsers: usersData?.totalUsers || 0,
    isGettingUsers,
    usersError,
    refetchUsers,
    setUsersQueryParams,
    getUsersWithParams,

    // Single user fetch
    useUserById,

    // Mutations
    createUser: createUserFn,
    isCreatingUser,
    updateUser: updateUserFn,
    isUpdatingUser,
    deleteUser: deleteUserFn,
    isDeletingUser,
    softDeleteUser: softDeleteUserFn,
    isSoftDeletingUser,
  };
}

export default useAdminUserFetch;
